import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaFile, FaLink, FaFont } from 'react-icons/fa';
import FileUploadUI from './FileUploadUI.js';
import LinkUI from './LinkUI.js';
import TextUI from './TextUI.js';
import ObservationUI from './ObservationUI.js';

/**
 * AttachmentArea Component
 * 
 * Renders an attachment interface for form questions. Supports file uploads, links,
 * text inputs, and observations. Dynamically creates tabs when multiple attachment
 * types are available, or renders single attachment types directly.
 * 
 * @param {object} question - The question object containing attachment configuration
 * @param {string} questionId - Unique identifier for the question
 * @param {object} answerFiles - Object containing uploaded files by question ID
 * @param {object} answerLinks - Object containing added links by question ID
 * @param {object} answerTexts - Object containing added texts by question ID
 * @param {object} activeAttachmentTab - Object tracking active tab by question ID
 * @param {function} setActiveAttachmentTab - Function to set active attachment tab
 * @param {boolean} readOnly - Whether the component is in read-only mode
 * @param {function} renderFileUploadUI - Function to render file upload interface
 * @param {function} renderLinkUI - Function to render link input interface
 * @param {function} renderTextUI - Function to render text input interface
 * @param {function} renderObservationUI - Function to render observation interface
 */
const AttachmentArea = ({
  question,
  questionId,
  answerFiles,
  answerLinks,
  answerTexts,
  activeAttachmentTab,
  setActiveAttachmentTab,
  readOnly = false,
  renderFileUploadUI,
  renderLinkUI,
  renderTextUI,
  renderObservationUI
}) => {
  const { t } = useTranslation();

  /**
   * Determines which attachment types are available for a question
   * based on the question's configuration properties
   * 
   * @param {object} question - The question object
   * @returns {Array} Array of available attachment type strings
   */
  const getAvailableAttachmentTypes = (question) => {
    const types = [];
    
    if (question.allowFileUpload) {
      types.push('file');
    }
    
    if (question.allowLinkUpload) {
      types.push('link');
    }
    
    if (question.allowTextUpload) {
      types.push('text');
    }
    
    return types;
  };

  /**
   * Initializes the active tab for a question if not already set
   * Defaults to the first available attachment type
   * 
   * @param {string} questionId - The question identifier
   * @param {Array} availableTypes - Array of available attachment types
   */
  const initializeActiveTab = (questionId, availableTypes) => {
    if (!activeAttachmentTab[questionId] && availableTypes.length > 0) {
      setActiveAttachmentTab(prev => ({
        ...prev,
        [questionId]: availableTypes[0]
      }));
    }
  };

  // Get available attachment types for this question
  const availableTypes = getAvailableAttachmentTypes(question);
  
  // Initialize active tab if needed (only if not in read-only mode)
  if (!readOnly) {
    initializeActiveTab(questionId, availableTypes);
  }
  
  // If no attachment types are available, check if observations are allowed
  if (availableTypes.length === 0) {
    if (question.allowObservations) {
      return renderObservationUI(questionId);
    }
    return null;
  }
  
  // If only one attachment type is available, render it directly without tabs
  if (availableTypes.length === 1) {
    const type = availableTypes[0];
    
    return (
      <>
        {/* Render the single available attachment type */}
        {type === 'file' && renderFileUploadUI(questionId, true, question)}
        {type === 'link' && renderLinkUI(questionId)}
        {type === 'text' && renderTextUI(questionId)}
        
        {/* Render observations after file/link/text if allowed */}
        {question.allowObservations && (
          <div className="mt-3">
            {renderObservationUI(questionId)}
          </div>
        )}
      </>
    );
  }
  
  // Multiple attachment types available, create tabbed interface
  return (
    <>
      <div className="attachment-area mt-3">
        <h6 className="small text-muted mb-2">{t('attachments', { ns: 'labels' })}</h6>
        
        {/* Tab navigation */}
        <ul className="nav nav-tabs" role="tablist">
          {availableTypes.map((type, index) => (
            <li className="nav-item" key={`tab-${questionId}-${type}`} role="presentation">
              <button 
                className={`nav-link ${activeAttachmentTab[questionId] === type ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  // Switch to the selected attachment type tab
                  setActiveAttachmentTab(prev => ({
                    ...prev,
                    [questionId]: type
                  }));
                }}
                role="tab"
                aria-selected={activeAttachmentTab[questionId] === type}
                disabled={readOnly}
              >
                {/* File attachment tab */}
                {type === 'file' && (
                  <>
                    <FaFile className="me-1" />
                    {t('file', { ns: 'labels' })}
                    {/* Show indicator badge if at least one file is uploaded */}
                    {answerFiles[questionId]?.length > 0 && <span className="badge bg-primary ms-2 rounded-circle">&nbsp;</span>}
                  </>
                )}
                
                {/* Link attachment tab */}
                {type === 'link' && (
                  <>
                    <FaLink className="me-1" />
                    {t('link', { ns: 'labels' })}
                    {/* Show indicator badge if link is added */}
                    {answerLinks[questionId] && <span className="badge bg-primary ms-2 rounded-circle">&nbsp;</span>}
                  </>
                )}
                
                {/* Text attachment tab */}
                {type === 'text' && (
                  <>
                    <FaFont className="me-1" />
                    {t('text', { ns: 'labels' })}
                    {/* Show indicator badge if text is added */}
                    {answerTexts[questionId] && <span className="badge bg-primary ms-2 rounded-circle">&nbsp;</span>}
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
        
        {/* Tab content */}
        <div className="tab-content">
          <div className="tab-pane fade show active">
            {/* Render content based on active tab */}
            {activeAttachmentTab[questionId] === 'file' && renderFileUploadUI(questionId, true)}
            {activeAttachmentTab[questionId] === 'link' && renderLinkUI(questionId)}
            {activeAttachmentTab[questionId] === 'text' && renderTextUI(questionId)}
          </div>
        </div>
      </div>
      
      {/* Render observations outside the tab interface if allowed */}
      {question.allowObservations && (
        <div className="mt-3">
          <h6 className="small text-muted mb-2">{t('observation', { ns: 'labels' })}</h6>
          {renderObservationUI(questionId)}
        </div>
      )}
    </>
  );
};

export default AttachmentArea; 